import React, { useEffect, useState } from "react"
import { Table, Input, Tag, Skeleton, Empty, Button } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import {
  dash,
  formatTimeSlot,
  formatType,
  formatQualifiedAt,
  sourceTag,
  leadSearchText,
} from "./leadsFormat"
import {
  loadLeads,
  clearLeadsCache,
  interpretLeadsAccessStatus,
} from "./leadsAccess"

// Leads: the merged ads + organic leads page at /app/leads. Reads GET /los/leads
// (JWT-scoped, one LO's leads only) and renders the seven fixed columns from
// AlaudaAI/contexts PR #15: Name, Email, Phone, Source, Type, Qualified at,
// Time slot. Fetch-all client-side table (D8), same paging/search shape as the
// Borrowers page. Nothing about /app/borrowers changes.

// Exported so a preview/render harness (and any future render test) can exercise
// the exact column definitions with mock rows.
export const columns = [
  {
    title: "Name",
    dataIndex: "Name",
    key: "name",
    render: (v) => dash(v),
  },
  {
    title: "Email",
    dataIndex: "Email",
    key: "email",
    render: (v) => dash(v),
  },
  {
    title: "Phone",
    dataIndex: "Phone",
    key: "phone",
    render: (v) => dash(v),
  },
  {
    title: "Source",
    dataIndex: "Source",
    key: "source",
    render: (v) => {
      const t = sourceTag(v)
      return <Tag color={t.color}>{t.label}</Tag>
    },
  },
  {
    title: "Type",
    dataIndex: "LeadType",
    key: "type",
    render: (v) => formatType(v),
  },
  {
    title: "Qualified at",
    dataIndex: "OccurredAt",
    key: "occurredAt",
    render: (v) => formatQualifiedAt(v),
    sorter: (a, b) =>
      new Date(a.OccurredAt || 0).getTime() - new Date(b.OccurredAt || 0).getTime(),
    defaultSortOrder: "descend",
    sortDirections: ["descend", "ascend", "descend"],
  },
  {
    title: "Time slot",
    dataIndex: "TimeSlot",
    key: "timeSlot",
    render: (v, record) => formatTimeSlot(v, record.Source),
  },
]

const Leads = () => {
  // allLeads is the immutable full list; leads is the search-filtered list bound
  // to the table (same dual-state pattern as Borrowers).
  const [allLeads, setAllLeads] = useState([])
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  // "enabled" | "notEnabled" | "unauthorized" | "unavailable". The pilot gate
  // (403) is a distinct, friendly state rather than an error: the nav entry is
  // already hidden for these LOs, so reaching here means a direct link or a
  // bookmark, and "something went wrong" would be a lie.
  const [access, setAccess] = useState("enabled")

  // Reads through the shared client, so arriving here from the portal reuses the
  // response the nav check already fetched instead of running the merged
  // ads+organic query a second time. Retry bypasses that cache on purpose.
  const getLeads = (force) => {
    setLoading(true)
    if (force) clearLeadsCache()
    loadLeads().then(({ status, body }) => {
      const state = interpretLeadsAccessStatus(status)
      setAccess(state)
      if (state === "enabled") {
        const rows = body && Array.isArray(body.Leads) ? body.Leads : []
        setAllLeads(rows)
        setLeads(rows)
      }
      setLoading(false)
    })
  }

  useEffect(() => {
    getLeads(false)
  }, [])

  const onSearch = (e) => {
    const v = e.target.value.toLowerCase()
    if (v === "") {
      setLeads(allLeads)
      return
    }
    const filtered = allLeads.filter((r) => leadSearchText(r).includes(v))
    setLeads(filtered)
  }

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    )
  }

  if (access === "notEnabled") {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="Leads isn't turned on for your account yet." />
      </div>
    )
  }

  if (access !== "enabled") {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="Could not load leads.">
          <Button type="primary" onClick={() => getLeads(true)}>
            Retry
          </Button>
        </Empty>
      </div>
    )
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Input
          style={{ width: 216, maxWidth: "100%" }}
          size="large"
          allowClear
          onChange={onSearch}
          placeholder="Search by any field"
          prefix={<SearchOutlined />}
        />
      </div>
      <Table
        rowKey="ID"
        columns={columns}
        dataSource={leads}
        showSorterTooltip={false}
        scroll={{ x: true }}
        locale={{ emptyText: <Empty description="No leads yet" /> }}
        pagination={{ hideOnSinglePage: true }}
      />
    </div>
  )
}

export default Leads
